import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { 
    Paperclip, Upload, Download, Trash2, FileText, Image, 
    FileSpreadsheet, File, Loader2, AlertCircle, Eye, Receipt, Layers, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { prospectsAPI, attachmentsAPI } from '@/lib/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { PrivateFileContent } from '@/components/shared/PrivateFileContent';
import { openPrivateFile } from '@/lib/privateFiles';
import { DocumentSlideshow } from './DocumentSlideshow';

const FILE_ICONS = {
    pdf: FileText,
    doc: FileText,
    docx: FileText,
    xls: FileSpreadsheet,
    xlsx: FileSpreadsheet,
    jpg: Image,
    jpeg: Image,
    png: Image,
    gif: Image,
};

const ATTACHMENT_TYPES = [
    { value: 'FACTURE', label: 'Facture', color: 'bg-green-100 text-green-700' },
    { value: 'AUTRE', label: 'Autre', color: 'bg-slate-100 text-slate-600' },
];

function getFileIcon(fileType) {
    return FILE_ICONS[fileType?.toLowerCase()] || File;
}

function formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export function ProspectAttachments({ prospectId, attachments = [], onRefresh, canDelete = true }) {
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [previewDialog, setPreviewDialog] = useState({ open: false, attachment: null });
    const [uploadType, setUploadType] = useState('AUTRE');
    const [updatingType, setUpdatingType] = useState(null);
    const [showAllDocsViewer, setShowAllDocsViewer] = useState(false);
    const fileInputRef = useRef(null);
    
    // Ouvrir tous les documents dans des fenêtres séparées
    const handleOpenAllDocs = () => {
        const viewableAttachments = attachments.filter(a => 
            ['jpg', 'jpeg', 'png', 'gif', 'pdf'].includes(a.file_type?.toLowerCase())
        );
        
        if (viewableAttachments.length === 0) {
            toast.info('Aucun document visualisable (PDF ou image)');
            return;
        }
        
        setShowAllDocsViewer(true);
    };

    const handleUpload = async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        
        setUploading(true);
        
        try {
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('attachment_type', uploadType);
                
                await prospectsAPI.uploadAttachment(prospectId, formData);
            }
            
            toast.success(files.length > 1 ? `${files.length} fichiers ajoutés` : 'Fichier ajouté');
            onRefresh?.();
        } catch (error) {
            console.error('Upload error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleDownload = async (attachment) => {
        try {
            const response = await attachmentsAPI.download(attachment.id);
            const blob = new Blob([response.data], { type: attachment.mime_type });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = attachment.original_filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Erreur lors du téléchargement');
        }
    };

    const handleDelete = async (attachmentId) => {
        if (!window.confirm('Supprimer ce fichier ?')) return;
        
        setDeleting(attachmentId);
        try {
            await attachmentsAPI.delete(attachmentId);
            toast.success('Fichier supprimé');
            onRefresh?.();
        } catch (error) {
            console.error('Delete error:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setDeleting(null);
        }
    };

    const handleTypeChange = async (attachmentId, newType) => {
        setUpdatingType(attachmentId);
        try {
            await api.put(`/attachments/${attachmentId}/type?attachment_type=${newType}`);
            toast.success(`Type modifié: ${newType}`);
            onRefresh?.();
        } catch (error) {
            console.error('Type update error:', error);
            toast.error('Erreur lors de la modification du type');
        } finally {
            setUpdatingType(null);
        }
    };

    const handlePreview = (attachment) => {
        // Pour les images et PDF, on peut prévisualiser
        if (['jpg', 'jpeg', 'png', 'gif', 'pdf'].includes(attachment.file_type?.toLowerCase())) {
            setPreviewDialog({ open: true, attachment });
        } else {
            handleDownload(attachment);
        }
    };

    const FileIcon = ({ fileType }) => {
        const Icon = getFileIcon(fileType);
        return <Icon className="w-5 h-5" />;
    };

    // Compter les factures
    const factureCount = attachments.filter(a => a.attachment_type === 'FACTURE').length;

    return (
        <Card data-testid="attachments-card">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                        <Paperclip className="w-5 h-5" />
                        Pièces jointes
                        {attachments.length > 0 && (
                            <Badge variant="secondary">{attachments.length}</Badge>
                        )}
                        {factureCount > 0 && (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                <Receipt className="w-3 h-3 mr-1" />
                                {factureCount} facture(s)
                            </Badge>
                        )}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        {/* Bouton pour ouvrir tous les documents */}
                        {attachments.length > 0 && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={handleOpenAllDocs}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                title="Voir tous les documents"
                                data-testid="open-all-docs-btn"
                            >
                                <Layers className="w-4 h-4 mr-2" />
                                Voir tout
                                <ExternalLink className="w-3 h-3 ml-1" />
                            </Button>
                        )}
                        {/* Sélecteur de type avant upload */}
                        <Select value={uploadType} onValueChange={setUploadType}>
                            <SelectTrigger className="w-32" data-testid="upload-type-select">
                                <SelectValue placeholder="Type" />
                            </SelectTrigger>
                            <SelectContent>
                                {ATTACHMENT_TYPES.map(type => (
                                    <SelectItem key={type.value} value={type.value}>
                                        {type.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx"
                            onChange={handleUpload}
                            className="hidden"
                            id="attachment-upload"
                        />
                        <Button
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            data-testid="upload-attachment-btn"
                        >
                            {uploading ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                                <Upload className="w-4 h-4 mr-2" />
                            )}
                            Ajouter
                        </Button>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                {attachments.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                        <Paperclip className="w-10 h-10 mx-auto mb-3 opacity-30" />
                        <p>Aucune pièce jointe</p>
                        <p className="text-xs mt-1">Formats acceptés: PDF, Images, Word, Excel</p>
                        <p className="text-xs text-yellow-600 mt-2">
                            Sélectionnez "Facture" avant d'uploader pour les factures
                        </p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Type</TableHead>
                                <TableHead>Fichier</TableHead>
                                <TableHead>Taille</TableHead>
                                <TableHead>Ajouté par</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {attachments.map((attachment) => {
                                const typeInfo = ATTACHMENT_TYPES.find(t => t.value === attachment.attachment_type) || ATTACHMENT_TYPES[1];
                                
                                return (
                                    <TableRow key={attachment.id} data-testid={`attachment-${attachment.id}`}>
                                        {/* Type avec dropdown pour changer */}
                                        <TableCell>
                                            <Select 
                                                value={attachment.attachment_type || 'AUTRE'} 
                                                onValueChange={(val) => handleTypeChange(attachment.id, val)}
                                                disabled={updatingType === attachment.id}
                                            >
                                                <SelectTrigger className={cn("w-28 h-7 text-xs", typeInfo.color)}>
                                                    {updatingType === attachment.id ? (
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                    ) : (
                                                        <SelectValue />
                                                    )}
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {ATTACHMENT_TYPES.map(type => (
                                                        <SelectItem key={type.value} value={type.value}>
                                                            {type.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <div className={cn(
                                                    "w-8 h-8 rounded flex items-center justify-center",
                                                    attachment.file_type === 'pdf' ? "bg-red-100 text-red-600" :
                                                    ['jpg', 'jpeg', 'png', 'gif'].includes(attachment.file_type) ? "bg-purple-100 text-purple-600" :
                                                    ['doc', 'docx'].includes(attachment.file_type) ? "bg-blue-100 text-blue-600" :
                                                    ['xls', 'xlsx'].includes(attachment.file_type) ? "bg-green-100 text-green-600" :
                                                    "bg-slate-100 text-slate-600"
                                                )}>
                                                    <FileIcon fileType={attachment.file_type} />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-sm truncate max-w-[200px]" title={attachment.original_filename}>
                                                        {attachment.original_filename}
                                                    </p>
                                                    <p className="text-xs text-slate-500 uppercase">{attachment.file_type}</p>
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-sm text-slate-500">
                                            {formatFileSize(attachment.file_size)}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                            {attachment.uploaded_by_name}
                                        </TableCell>
                                        <TableCell className="text-sm text-slate-500">
                                            {format(new Date(attachment.created_at), "dd MMM yyyy", { locale: fr })}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                {['jpg', 'jpeg', 'png', 'gif', 'pdf'].includes(attachment.file_type?.toLowerCase()) && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handlePreview(attachment)}
                                                        title="Aperçu"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => handleDownload(attachment)}
                                                    title="Télécharger"
                                                    data-testid={`download-${attachment.id}`}
                                                >
                                                    <Download className="w-4 h-4" />
                                                </Button>
                                                {canDelete && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDelete(attachment.id)}
                                                        disabled={deleting === attachment.id}
                                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        title="Supprimer"
                                                        data-testid={`delete-${attachment.id}`}
                                                    >
                                                        {deleting === attachment.id ? (
                                                            <Loader2 className="w-4 h-4 animate-spin" />
                                                        ) : (
                                                            <Trash2 className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </CardContent>

            {/* Preview Dialog pour images */}
            <Dialog 
                open={previewDialog.open && previewDialog.attachment?.file_type !== 'pdf'} 
                onOpenChange={(open) => setPreviewDialog({ open, attachment: null })}
            >
                <DialogContent className="max-w-4xl max-h-[90vh]">
                    <DialogHeader>
                        <DialogTitle>{previewDialog.attachment?.original_filename}</DialogTitle>
                    </DialogHeader>
                    <div className="overflow-auto max-h-[70vh]">
                        {['jpg', 'jpeg', 'png', 'gif'].includes(previewDialog.attachment?.file_type?.toLowerCase()) && (
                            <div className="flex justify-center">
                                <PrivateFileContent image testId="attachment-image-preview"
                                    source={`/attachments/${previewDialog.attachment?.id}/download`}
                                    title={previewDialog.attachment?.original_filename}
                                    className="max-w-full max-h-[60vh] object-contain"
                                />
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewDialog({ open: false, attachment: null })}>
                            Fermer
                        </Button>
                        <Button onClick={() => handleDownload(previewDialog.attachment)}>
                            <Download className="w-4 h-4 mr-2" />
                            Télécharger
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* PDF Preview Modal pour les PDF */}
            {previewDialog.open && previewDialog.attachment?.file_type === 'pdf' && (
                <Dialog 
                    open={true} 
                    onOpenChange={() => setPreviewDialog({ open: false, attachment: null })}
                >
                    <DialogContent className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0">
                        <DialogHeader className="px-4 py-3 border-b bg-slate-50 flex-shrink-0">
                            <div className="flex items-center justify-between">
                                <DialogTitle className="flex items-center gap-2 text-lg">
                                    <FileText className="w-5 h-5 text-blue-500" />
                                    Aperçu - {previewDialog.attachment?.original_filename || 'Document'}
                                </DialogTitle>
                                <div className="flex items-center gap-2">
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={() => openPrivateFile(`/attachments/${previewDialog.attachment?.id}/view`)}
                                        className="gap-1"
                                    >
                                        <ExternalLink className="w-4 h-4" />
                                        Nouvel onglet
                                    </Button>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        onClick={() => handleDownload(previewDialog.attachment)}
                                        className="gap-1"
                                    >
                                        <Download className="w-4 h-4" />
                                        Télécharger
                                    </Button>
                                </div>
                            </div>
                        </DialogHeader>
                        
                        <div className="flex-1 overflow-auto bg-slate-200 p-4">
                            <div className="flex justify-center h-full">
                                <PrivateFileContent testId="attachment-pdf-preview"
                                    source={`/attachments/${previewDialog.attachment?.id}/view`}
                                    className="w-full bg-white shadow-lg rounded"
                                    style={{ 
                                        height: '100%',
                                        minHeight: '600px',
                                        maxWidth: '900px'
                                    }}
                                    title={previewDialog.attachment?.original_filename || 'Document PDF'}
                                />
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
            <DocumentSlideshow isOpen={showAllDocsViewer} onClose={() => setShowAllDocsViewer(false)}
                documents={attachments.filter(a => ['jpg', 'jpeg', 'png', 'gif', 'pdf'].includes(a.file_type?.toLowerCase()))}
                onDownload={handleDownload} />
        </Card>
    );
}
